import { Icon } from "@iconify/react/dist/iconify.js";
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Checkbox, Collapse, FormControlLabel, Grow, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/logos/icare_fulllogo.png';
import { AppContext } from "../../Base";
import TermsAndConModal from "../../components/infomodals/TermsAndConModal";
import { ButtonContentWithLoader } from "../../components/inputs/ButtonContentWithLoader";
import PasswordInput from "../../components/inputs/PasswordInput";
import { isOTPValid, isValidEmail, readCookie, sendReactGAPageView } from "../../consts/helpers";

export default function CreateAccount() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Create New Account');
    }, [pathname])

    const navigate = useNavigate();
    const setPoints = useContext(AppContext).setPoints;
    const setFootprint = useContext(AppContext).setFootprint;
    const [siteversion, setSiteVersion] = useContext(AppContext).siteversionState;
    const setSessionCookie = useContext(AppContext).setSessionCookie;

    // Inputs
    const [email, setEmail] = useState('');
    const [otp, setOTP] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [agreeTerms, setAgreeTerms] = useState(false); 

    // Stage flags
    const [isLoading, setLoading] = useState(false);
    const [accountCreationStage, setStage] = useState(1); // 1: email, 2: verification code, 3: password
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isOtpCorrect, setIsOtpCorrect] = useState(false);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isPasswordRegexValid, setIsPasswordRegexValid] = useState(false);

    // Response flags
    const [isAlertOpen, setOpen] = useState(false);
    const [apiResponse, setResponse] = useState({});
    const setAlertResponse = (severity, message) => {
        setResponse({ severity: severity, message: message });
        setOpen(Boolean(message));
        setTimeout(() => {
            setOpen(false);
        }, 20000);
    }

    const handleEmailChange = (e) => {
        const emailInput = e.target.value.toLowerCase();
        setEmail(emailInput);
        setIsEmailValid(isValidEmail(emailInput));  
    };


    const setErrorMsg = (msg) => {
        if (msg) setAlertResponse('warning', msg);
    }

    const callSignUpAPI = async (stage) => {
        const url = process.env.REACT_APP_NEWUSER_LAMBDA;
        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                'stage': stage,
                'email': email,
                'code': otp,
                'password': password,
                'username': username,
                'siteversion': siteversion,
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
        return response.json();
    }

    const handleAccountCreation = (e, overrideStage1 = false) => {
        e.preventDefault();
        var stage = overrideStage1 ? 1 : accountCreationStage;

        // Catch potential errors from incomplete input fields
        if (stage === 1 && (email === "" || !isValidEmail(email))) {
            setErrorMsg("Please enter a valid email.");
            return;
        } else if (stage === 2 && otp === "") {
            setErrorMsg("Please enter the verification code sent to your email.");
            return;
        } else if (stage === 3) {
            if (username.length < 5) {
                setErrorMsg("Please enter a longer username: minimum 5 characters.");
                return;
            }
            
            if (password === "") {
                setErrorMsg("Please enter a password.");
                return;
            }
            else if (username === "" || password === "") {
                setErrorMsg("Please enter your username and a password.");
                return;
            }
        } 
        setAlertResponse('success','')
        setLoading(true);
        callSignUpAPI(stage).then(
            function(data) {
                setLoading(false);
                if (data?.passStage) {
                    if (stage === 1) {
                        if (data?.isNewEmail) {
                            setAlertResponse('success','An OTP has been sent to your email. Enter it above to verify your access and create your account.')
                            setIsOtpSent(true); 
                            setStage(2);
                        } else {
                            setAlertResponse('warning','This email has already been used to create an account. Please log in.')
                        }
                    } else if (stage === 2) {
                        setStage(3);
                        setIsOtpCorrect(true); 
                    } else if (stage === 3) {
                        // console.log(data)
                        if (data?.loggedIn) {
                            setSessionCookie(data?.userData.email, data?.token)
                            setPoints(data?.userData?.totalPoints);
                            setFootprint(data?.userData?.carbonFootprint);
                            navigate('/');
                        } else {
                            setAlertResponse('success','Account was successfully created. You may now log in.')
                            setStage(4);
                        }
                    } 
                } else {
                    setErrorMsg(data?.message);
                }
            }
        ).catch(
            function(error) {
                console.log(error)
                setErrorMsg(error);
            }
        );
    }

    useEffect(()=>{
        if (siteversion === null) setSiteVersion(readCookie('icare_siteversion'));
    },[siteversion])

    return (
        <Stack spacing={3}>
            <Box className="centered-container">
                <img src={logo} alt='icare app logo' style={{ maxWidth: 300 }}/>
            </Box>
            <Typography variant="h4" align="center" className='green-text-color'>Create New Account</Typography>
            {accountCreationStage !== 4 ? (
                <>
                    <TextField
                        className='rounded-input' 
                        fullWidth
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={handleEmailChange}
                        margin="normal"
                        required
                        disabled={isOtpSent}
                        slotProps={{
                            input: {
                                endAdornment:(
                                    <Grow in={isOtpCorrect}><Icon icon='gg:check-o' color='var(--pri-green)' fontSize={'32px'} /></Grow>
                                )
                            }
                        }}
                        error={!isEmailValid && email.length > 0}  
                        helperText={!isEmailValid && email.length > 0 ? "Please enter a valid email address" : ""} 
                    />
                    { accountCreationStage===1 ? ( 
                        <Button 
                            className={`pill_btn ${isEmailValid ? 'pri_btn' : ''}`}
                            fullWidth 
                            onClick={handleAccountCreation}
                            disabled={!isValidEmail(email)}
                        >
                            { ButtonContentWithLoader(isLoading, 'Get Verification Code') }
                        </Button>
                    ) : 
                    accountCreationStage===2 ? (
                        <>
                        <TextField
                            className='rounded-input' 
                            fullWidth
                            label="OTP"
                            variant="outlined"
                            value={otp}
                            onChange={(e) => setOTP(e.target.value)}
                            margin="normal"
                            required
                            error={!isOTPValid(otp)}  
                            helperText={!isOTPValid(otp) ? "Invalid code. Please enter the 4-digit OTP sent to your email." : ""} 
                        />
                        <Button className="pill_btn pri_btn" fullWidth onClick={handleAccountCreation}>
                            { ButtonContentWithLoader(isLoading, 'Verify') }
                        </Button>
                        </>
                    ) : (
                        <>
                        <TextField
                            className='rounded-input' 
                            fullWidth
                            label="Display name"
                            variant="outlined"
                            value={username}
                            onChange={ (e) => {setUsername(e.target.value.trim())}}
                            margin="normal"
                            required
                            disabled={(accountCreationStage === 4)}
                            error={username.length > 45}  
                            helperText={username.length > 45 ? "Username is too long!" : ""} 
                        />
                        <PasswordInput
                            password={password}
                            changeHandler={(e) => setPassword(e.target.value.trim())}
                            setIsPasswordRegexValid={setIsPasswordRegexValid}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={agreeTerms}
                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                />
                            }
                            label={
                                <TermsAndCondLink />
                            }
                        />
                        <Button 
                            className={`pill_btn ${(username && password && agreeTerms) ? 'pri_btn' : (accountCreationStage === 4) ? 'Mui-disabled' : ''}`}
                            fullWidth 
                            onClick={handleAccountCreation}
                            disabled={!username || !password || !isPasswordRegexValid || !agreeTerms || accountCreationStage === 4}
                        >
                            { ButtonContentWithLoader(isLoading, 'Sign Up') }
                        </Button>
                        </>
                    )}
                    <Collapse in={isAlertOpen}>
                        <Alert
                        action={
                            <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setOpen(false);
                            }}
                            >
                            <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mt: 2, borderRadius: '24px' }}
                        severity={apiResponse.severity}
                        >
                        {apiResponse.message}
                        </Alert>
                    </Collapse>
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                        Already have an account? <Link to="/login">Login here!</Link>
                    </Typography>
                </>
            ) : (
                <>
                <Box sx={{ textAlign: 'center'}}>
                    <Typography variant="h1">
                        <Icon icon='gg:check-o' color='var(--pri-green)' />
                    </Typography>
                    <Typography variant="body1">
                        Your account has been<br /> created successfully!
                    </Typography>
                </Box>
                <Button
                    className={'pill_btn pri_btn'}
                    fullWidth
                    onClick={() => navigate('/login')}
                >
                    Proceed to log in
                </Button>
            </>
        )}
        </Stack>
    );
}

const TermsAndCondLink = () => {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <>
        <Typography variant="body2">
            I agree to the <Link onClick={handleOpen}>Terms and Conditions</Link>
        </Typography>

        <TermsAndConModal open={open} handleClose={handleClose} />
        </>
    )
}